import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthContext } from './useAuthContext'
import useRedirectToAuth from './useRedirectToAuth'
import useGetManagerTeam from './useGetManagerTeam'

/**
 * Hook to sync user permissions in real-time
 * Checks every minute and on window focus whether the role or the clinic changed and updates the user context
 */
const usePermissionSync = () => {
  const { dispatch, user } = useAuthContext()
  const redirectToAuth = useRedirectToAuth()
  const { getManagerTeam } = useGetManagerTeam()

  useQuery({
    queryKey: ['permissionSync', user?._id],
    queryFn: async () => {
      const response = await fetch(`/api/health/api/users/${user._id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      })

      const json = await response.json()

      if (!response.ok) throw new Error(json.error)

      // NOTE: the clinic in context is the one the user manages, else the one their account belongs to
      const team = (await getManagerTeam(user.email, user.token)) ?? json.team ?? ''

      const hasChanged =
        json.admin !== user.admin ||
        json.clinician !== user.clinician ||
        String(team?._id ?? '') !== String(user.team?._id ?? '') ||
        team?.updatedAt !== user.team?.updatedAt

      if (hasChanged) {
        dispatch({
          type: 'UPDATE_USER',
          payload: {
            admin: json.admin,
            clinician: json.clinician,
            team
          }
        })
      }

      return json ?? null
    },
    enabled: !!user?._id && !!user?.token,
    refetchInterval: 60 * 1000, // NOTE: Check every 1 minute
    refetchOnWindowFocus: true, // NOTE: Check when user returns to tab
    staleTime: 30 * 1000 // NOTE: Consider how long the data is stale after 30 seconds
  })

  useEffect(() => {
    if (!user?._id || !user?.token) redirectToAuth()
  }, [user?._id, user?.token, redirectToAuth])
}

export default usePermissionSync
